import type { ReactElement } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Cancel,
  CheckCircle,
  EditNote,
  HourglassTop,
  PauseCircle,
  PauseCircleOutline,
  PlayCircle,
  TaskAlt,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { CampaignStatus } from '../types';

type CampaignStatusValue = CampaignStatus | 'suspended';
type StatusColor = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';

interface CampaignStatusChipProps {
  status?: CampaignStatusValue;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  description?: string;
}

const statusConfig: Record<CampaignStatusValue, { labelKey: string; color: StatusColor; icon: ReactElement }> = {
  draft: { labelKey: 'components:statusChips.campaign.labels.draft', color: 'default', icon: <EditNote sx={{ fontSize: 16 }} /> },
  processing: { labelKey: 'components:statusChips.campaign.labels.processing', color: 'info', icon: <HourglassTop sx={{ fontSize: 16 }} /> },
  ready: { labelKey: 'components:statusChips.campaign.labels.ready', color: 'primary', icon: <TaskAlt sx={{ fontSize: 16 }} /> },
  active: { labelKey: 'components:statusChips.campaign.labels.active', color: 'success', icon: <PlayCircle sx={{ fontSize: 16 }} /> },
  running: { labelKey: 'components:statusChips.campaign.labels.running', color: 'success', icon: <PlayCircle sx={{ fontSize: 16 }} /> },
  suspended: { labelKey: 'components:statusChips.campaign.labels.suspended', color: 'warning', icon: <PauseCircleOutline sx={{ fontSize: 16 }} /> },
  paused: { labelKey: 'components:statusChips.campaign.labels.paused', color: 'warning', icon: <PauseCircle sx={{ fontSize: 16 }} /> },
  completed: { labelKey: 'components:statusChips.campaign.labels.completed', color: 'info', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
  cancelled: { labelKey: 'components:statusChips.campaign.labels.cancelled', color: 'error', icon: <Cancel sx={{ fontSize: 16 }} /> },
};

function getPaletteColor(theme: Theme, color: StatusColor) {
  if (color === 'default') {
    return {
      bg: theme.palette.action.selected,
      text: theme.palette.text.secondary,
    };
  }
  return {
    bg: theme.palette[color].light,
    text: theme.palette[color].contrastText,
  };
}

export default function CampaignStatusChip({ status, size = 'small', sx, description }: CampaignStatusChipProps) {
  const theme = useTheme();
  const { t } = useTranslation(['components']);

  if (!status) return null;
  const config = statusConfig[status];
  const tooltipText = description || t(`components:statusChips.campaign.descriptions.${status}`);
  const palette = getPaletteColor(theme, config.color);

  return (
    <Tooltip title={tooltipText} arrow>
      <Box component="span">
        <Chip
          icon={config.icon}
          label={t(config.labelKey, status)}
          size={size}
          sx={{
            bgcolor: palette.bg,
            color: palette.text,
            fontWeight: 600,
            textTransform: 'none',
            '& .MuiChip-icon': { fill: palette.text },
            ...sx,
          }}
        />
      </Box>
    </Tooltip>
  );
}
